import { useState } from "react";
import { CheckCircle2, AlertCircle, Send } from "lucide-react";
import { useLang, useL } from "@/context/LanguageContext";
import { GOVERNORATES } from "@/data/copy";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const TOPICS = ["product-info", "technical-support", "distribution", "pricing"];

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  farm: "",
  governorate: "",
  topic: "product-info",
  product: "",
  message: "",
  website: "", // honeypot
};

const fieldClass =
  "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink outline-none transition-colors focus:border-forest";

const Field = ({ id, label, error, required, children }) => (
  <div>
    <label htmlFor={id} className="mb-2 block text-xs font-semibold uppercase tracking-wider text-stone">
      {label}
      {required && <span className="text-gold-ink"> *</span>}
    </label>
    {children}
    {error && (
      <p id={`${id}-error`} role="alert" className="mt-1.5 text-xs font-medium text-destructive">
        {error}
      </p>
    )}
  </div>
);

/**
 * Public inquiry form. Validates client-side for fast feedback, but the API
 * revalidates everything — this is convenience, not a security boundary.
 */
export const InquiryForm = ({ defaultProduct = "" }) => {
  const { t, lang } = useLang();
  const L = useL();
  const f = t.form;

  const [values, setValues] = useState({ ...EMPTY, product: defaultProduct });
  const [errors, setErrors] = useState({});
  const [state, setState] = useState("idle"); // idle | sending | sent | error
  const [submitError, setSubmitError] = useState(null);

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  const validate = () => {
    const next = {};
    if (values.name.trim().length < 2) next.name = f.required;
    const digits = values.phone.replace(/\D/g, "");
    if (!values.phone.trim()) next.phone = f.required;
    else if (digits.length < 8) next.phone = f.invalidPhone;
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      next.email = f.invalidEmail;
    }
    if (!values.message.trim()) next.message = f.required;
    else if (values.message.trim().length < 10) next.message = f.tooShort;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);
    if (!validate()) return;

    setState("sending");
    try {
      const res = await fetch(`${API_BASE}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          email: values.email || null,
          farm: values.farm || null,
          governorate: values.governorate || null,
          product: values.product || null,
          lang,
        }),
      });

      if (res.status === 429) {
        setSubmitError(f.errorRateLimit);
        setState("error");
        return;
      }
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);

      setState("sent");
      setValues({ ...EMPTY, product: defaultProduct });
    } catch (err) {
      setSubmitError(f.errorGeneric);
      setState("error");
    }
  };

  if (state === "sent") {
    return (
      <div
        className="rounded-2xl border border-forest/20 bg-white p-8 text-center"
        data-testid="inquiry-success"
        role="status"
      >
        <CheckCircle2 className="mx-auto h-12 w-12 text-forest" strokeWidth={1.6} />
        <h3 className="mt-5 font-display text-2xl font-bold text-forest">{f.successTitle}</h3>
        <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-stone">{f.successBody}</p>
        <button
          type="button"
          onClick={() => setState("idle")}
          className="mt-6 rounded-full border border-forest/30 px-6 py-2.5 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-cream"
        >
          {f.sendAnother}
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate data-testid="inquiry-form" className="grid gap-5 sm:grid-cols-2">
      <Field id="inq-name" label={f.name} error={errors.name} required>
        <input
          id="inq-name"
          name="name"
          autoComplete="name"
          value={values.name}
          onChange={set("name")}
          aria-invalid={errors.name ? "true" : undefined}
          aria-describedby={errors.name ? "inq-name-error" : undefined}
          className={fieldClass}
        />
      </Field>

      <Field id="inq-phone" label={f.phone} error={errors.phone} required>
        <input
          id="inq-phone"
          name="phone"
          type="tel"
          dir="ltr"
          inputMode="tel"
          autoComplete="tel"
          value={values.phone}
          onChange={set("phone")}
          aria-invalid={errors.phone ? "true" : undefined}
          aria-describedby={errors.phone ? "inq-phone-error" : undefined}
          className={`${fieldClass} text-start`}
        />
      </Field>

      <Field id="inq-email" label={f.email} error={errors.email}>
        <input
          id="inq-email"
          name="email"
          type="email"
          dir="ltr"
          autoComplete="email"
          value={values.email}
          onChange={set("email")}
          aria-invalid={errors.email ? "true" : undefined}
          aria-describedby={errors.email ? "inq-email-error" : undefined}
          className={`${fieldClass} text-start`}
        />
      </Field>

      <Field id="inq-farm" label={f.farm}>
        <input
          id="inq-farm"
          name="farm"
          autoComplete="organization"
          value={values.farm}
          onChange={set("farm")}
          className={fieldClass}
        />
      </Field>

      <Field id="inq-governorate" label={f.governorate}>
        <select
          id="inq-governorate"
          name="governorate"
          value={values.governorate}
          onChange={set("governorate")}
          className={fieldClass}
        >
          <option value="">{f.governoratePlaceholder}</option>
          {GOVERNORATES.map((g) => (
            <option key={g.id} value={g.id}>
              {L(g)}
            </option>
          ))}
        </select>
      </Field>

      <Field id="inq-topic" label={f.topic} required>
        <select
          id="inq-topic"
          name="topic"
          value={values.topic}
          onChange={set("topic")}
          className={fieldClass}
        >
          {TOPICS.map((id) => (
            <option key={id} value={id}>
              {f.topics[id]}
            </option>
          ))}
        </select>
      </Field>

      <div className="sm:col-span-2">
        <Field id="inq-product" label={f.product}>
          <input
            id="inq-product"
            name="product"
            value={values.product}
            onChange={set("product")}
            className={fieldClass}
          />
        </Field>
      </div>

      <div className="sm:col-span-2">
        <Field id="inq-message" label={f.message} error={errors.message} required>
          <textarea
            id="inq-message"
            name="message"
            rows={5}
            value={values.message}
            onChange={set("message")}
            placeholder={f.messagePlaceholder}
            aria-invalid={errors.message ? "true" : undefined}
            aria-describedby={errors.message ? "inq-message-error" : undefined}
            className={`${fieldClass} resize-y`}
          />
        </Field>
      </div>

      {/* Honeypot — hidden from people, tempting to bots. */}
      <div aria-hidden="true" className="hidden">
        <label htmlFor="inq-website">Website</label>
        <input
          id="inq-website"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={set("website")}
        />
      </div>

      {submitError && (
        <div
          role="alert"
          data-testid="inquiry-error"
          className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive sm:col-span-2"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{submitError}</span>
        </div>
      )}

      <div className="sm:col-span-2">
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid="inquiry-submit"
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-forest px-6 py-4 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-forest-dark disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 sm:w-auto sm:px-10"
        >
          <Send aria-hidden="true" className="h-4 w-4 rtl-flip" />
          {state === "sending" ? f.sending : f.submit}
        </button>
      </div>
    </form>
  );
};
